import { useState } from 'react';
import { useNavigate } from 'react-router';
import clsx from 'clsx';

import { Button, Card, Icon, Switch } from '@lib/ui';
import { Input, Textarea } from '@lib/ui';
import type { SimpleSource, SimpleCapability } from '../types';
import { defaultSimpleSource, LEGACY_SCENARIOS } from '../types';
import { SourcePreview } from '../components/SourcePreview';

export default function SimpleWizard() {
  const navigate = useNavigate();
  const [source, setSource] = useState<SimpleSource>(defaultSimpleSource);
  const [activeScenario, setActiveScenario] = useState<string | null>(null);

  const updateSource = (updates: Partial<SimpleSource>) => {
    setSource((prev) => ({ ...prev, ...updates }));
  };

  const toggleCapability = (cap: SimpleCapability) => {
    const current = source.capabilities;
    const updated = current.includes(cap) ? current.filter((c) => c !== cap) : [...current, cap];

    // Clear config when capability is removed
    const newConfig = { ...source.capabilityConfig };
    if (!updated.includes(cap)) {
      delete newConfig[cap];
    }

    updateSource({ capabilities: updated, capabilityConfig: newConfig });
  };

  const updateCapabilityConfig = (cap: SimpleCapability, config: Record<string, string>) => {
    updateSource({
      capabilityConfig: {
        ...source.capabilityConfig,
        [cap]: config,
      },
    });
  };

  const loadScenario = (scenarioId: string) => {
    setActiveScenario(scenarioId);
    switch (scenarioId) {
      case 'pure-llm':
        setSource({
          title: 'Daily Motivation',
          prompt: 'Write a motivational quote about perseverance',
          capabilities: [],
          capabilityConfig: {},
        });
        break;
      case 'url-summary':
        setSource({
          title: 'Article Summary',
          prompt: 'Summarize the main points from this article in 3 bullet points',
          capabilities: ['web_fetch'],
          capabilityConfig: {
            web_fetch: { url: 'https://example.com/article' },
          },
        });
        break;
      case 'site-discovery':
        setSource({
          title: 'Competitor Pricing',
          prompt: 'Find the pricing page and extract all pricing tiers with their features',
          capabilities: ['web_discover'],
          capabilityConfig: {
            web_discover: { baseUrl: 'https://competitor.com', hint: 'pricing' },
          },
        });
        break;
      case 'complex-extraction':
        setSource({
          title: 'Product Reviews',
          prompt: `Extract all user comments from this product page.
For each comment capture:
- Username
- Rating (1-5 stars)
- Date
- Comment text

Then summarize the overall sentiment.`,
          capabilities: ['web_fetch'],
          capabilityConfig: {
            web_fetch: { url: 'https://amazon.com/product/123' },
          },
        });
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-600 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-500 w-10 h-10 rounded-lg flex items-center justify-center">
              <Icon name="folder" className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Simple Model</h1>
              <p className="text-sm text-gray-500 dark:text-dark-400">
                Flat capabilities with static configuration
              </p>
            </div>
          </div>
          <Button variant="flat" onClick={() => navigate('/poc/source-modeling')}>
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Scenario selector */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Load a scenario:
          </p>
          <div className="flex flex-wrap gap-2">
            {LEGACY_SCENARIOS.map((scenario) => (
              <button
                key={scenario.id}
                onClick={() => loadScenario(scenario.id)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeScenario === scenario.id
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-dark-300 hover:bg-gray-200 dark:hover:bg-dark-600'
                )}
              >
                {scenario.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <Card skin="shadow" className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Configure Source
            </h2>

            <div className="space-y-6">
              {/* Title */}
              <Input
                label="Title"
                placeholder="e.g., Article Summarizer"
                value={source.title}
                onChange={(e) => updateSource({ title: e.target.value })}
              />

              {/* Prompt */}
              <Textarea
                label="Prompt"
                placeholder="What should the LLM do with the content?"
                rows={4}
                value={source.prompt}
                onChange={(e) => updateSource({ prompt: e.target.value })}
                description="The scraped content will be automatically prepended to this prompt"
              />

              {/* Capabilities */}
              <div>
                <label className="text-sm font-medium text-fg mb-3 block">Capabilities</label>
                <div className="space-y-4">
                  {/* Web Fetch */}
                  <div className="p-4 rounded-lg border border-gray-200 dark:border-dark-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Web Fetch</p>
                        <p className="text-sm text-gray-500 dark:text-dark-400">
                          Scrape content from a single URL
                        </p>
                      </div>
                      <Switch
                        checked={source.capabilities.includes('web_fetch')}
                        onChange={() => toggleCapability('web_fetch')}
                        color="success"
                      />
                    </div>
                    {source.capabilities.includes('web_fetch') && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-600">
                        <Input
                          label="URL"
                          placeholder="https://example.com/article"
                          value={source.capabilityConfig.web_fetch?.url || ''}
                          onChange={(e) =>
                            updateCapabilityConfig('web_fetch', { url: e.target.value })
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Web Discover */}
                  <div className="p-4 rounded-lg border border-gray-200 dark:border-dark-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Web Discover</p>
                        <p className="text-sm text-gray-500 dark:text-dark-400">
                          Crawl and discover pages within a site
                        </p>
                      </div>
                      <Switch
                        checked={source.capabilities.includes('web_discover')}
                        onChange={() => toggleCapability('web_discover')}
                        color="success"
                      />
                    </div>
                    {source.capabilities.includes('web_discover') && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-600 space-y-4">
                        <Input
                          label="Base URL"
                          placeholder="https://example.com"
                          value={source.capabilityConfig.web_discover?.baseUrl || ''}
                          onChange={(e) =>
                            updateCapabilityConfig('web_discover', {
                              ...source.capabilityConfig.web_discover,
                              baseUrl: e.target.value,
                            })
                          }
                        />
                        <Input
                          label="Discovery Hint (optional)"
                          placeholder="e.g., pricing, about, docs"
                          value={source.capabilityConfig.web_discover?.hint || ''}
                          onChange={(e) =>
                            updateCapabilityConfig('web_discover', {
                              ...source.capabilityConfig.web_discover,
                              hint: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Preview */}
          <div className="space-y-6">
            <SourcePreview title="Data Structure" data={source} format="json" />
            <SourcePreview title="User Config (YAML)" data={source} format="yaml" />
          </div>
        </div>
      </div>
    </div>
  );
}
